/**
 * 音效引擎(方案 F6 · 程序化合成,零外部音频资产)。
 *
 * 设计:
 *  - 全部声音由 WebAudio 实时合成(噪声源 + 滤波 + 包络),不加载任何音频文件——
 *    与全项目"零外部美术资产"同一哲学,也免去素材版权问题。
 *  - 浏览器 autoplay 政策:AudioContext 必须在用户手势后创建/恢复。模块挂全局
 *    pointerdown/keydown 一次性监听,首次交互即解锁并淡入环境底床。
 *  - 三层结构:环境底床(风,常驻) / 天气 loop(雨·暴雨·风,可叠加淡入淡出) /
 *    事件 one-shot(雷·烟花·流星·钟·凿·币)。所有增益都刻意保守——声音是衬托,
 *    B 站视频/直播才是主声源。
 *  - 对外契约与旧占位版完全一致:sfx.loop / sfx.play / sfx.stop,
 *    另加 sfx.setEnabled / sfx.enabled(HUD 静音开关用,localStorage 持久化)。
 */

type LoopName = 'rain' | 'storm' | 'wind';
type OneShot = 'boom' | 'thunder' | 'meteor' | 'coin' | 'bell' | 'carve' | 'chime';

const LS_KEY = 'lingxu-sound';
/** BGM 循环曲目(放入 web/public/audio/;缺文件时自动静默跳过) */
const MUSIC_TRACKS = ['/audio/bgm_loop_a.mp3', '/audio/bgm_loop_b.mp3'];
const MUSIC_VOL = 0.16;

class SfxEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private loops = new Map<string, { gain: GainNode; src: AudioBufferSourceNode; lfo?: OscillatorNode }>();
  private ambientOn = false;
  private _enabled = localStorage.getItem(LS_KEY) !== 'off';

  constructor() {
    // 首次手势解锁(once)。已静音也先解锁 ctx,便于随时打开。
    const unlock = () => {
      this.ensureCtx();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  get enabled() { return this._enabled; }
  setEnabled(on: boolean) {
    this._enabled = on;
    localStorage.setItem(LS_KEY, on ? 'on' : 'off');
    if (this.master && this.ctx) {
      this.master.gain.linearRampToValueAtTime(on ? 0.8 : 0, this.ctx.currentTime + 0.3);
    }
    if (this.music) this.music.volume = on ? MUSIC_VOL : 0;
    if (on) this.ensureCtx();
  }

  // ── BGM:圣咏系 ambient loop(与方向A同声音语言),<audio> 循环通道 ──
  // 音量刻意压低(0.16):BGM 是空气,神迹效果音(WebAudio 合成,走 master 0.8)
  // 必须永远清晰浮在其上。效果音与 BGM 分属两条通道,互不遮蔽。
  private music: HTMLAudioElement | null = null;
  private startMusic() {
    if (this.music || !MUSIC_TRACKS.length) return;
    const pick = MUSIC_TRACKS[Math.floor(Math.random() * MUSIC_TRACKS.length)];
    const a = new Audio(pick);
    a.loop = true;
    a.volume = this._enabled ? MUSIC_VOL : 0;
    a.play().catch(() => { this.music = null; });   // 文件缺失/未解锁时静默放弃
    this.music = a;
  }

  private ensureCtx() {
    this.startMusic();
    if (this.ctx) { if (this.ctx.state === 'suspended') void this.ctx.resume(); this.startAmbient(); return; }
    try {
      this.ctx = new AudioContext();
    } catch { return; }
    this.master = this.ctx.createGain();
    this.master.gain.value = this._enabled ? 0.8 : 0;
    this.master.connect(this.ctx.destination);
    // 录制支持(record-ep1.js):把 master 并联导出为 MediaStream,
    // 免录屏成片时与画面流/旁白流合轨。不影响正常出声,常驻无害。
    try {
      const rd = this.ctx.createMediaStreamDestination();
      this.master.connect(rd);
      (window as unknown as { __sfxStream?: MediaStream }).__sfxStream = rd.stream;
    } catch { /* 老浏览器无此 API 时静默 */ }
    // 2s 白噪 buffer,全部噪声系声音共用
    const len = this.ctx.sampleRate * 2;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.startAmbient();
  }

  /** 环境底床:极轻的风(带 LFO 游走的带通噪声)。世界"一直在响",但几乎听不见。 */
  private startAmbient() {
    if (this.ambientOn || !this.ctx || !this.master || !this.noiseBuf) return;
    this.ambientOn = true;
    const { src, gain } = this.noiseLoop(320, 0.6, 0.028, 6);
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.frequency.value = 0.09; lfoGain.gain.value = 130;
    lfo.connect(lfoGain);
    const bp = (src as unknown as { _bp?: BiquadFilterNode })._bp;
    if (bp) lfoGain.connect(bp.frequency);
    lfo.start();
    this.loops.set('__ambient', { gain, src, lfo });
  }

  /** 建一条 噪声→带通/低通→gain→master 的 loop,返回节点。 */
  private noiseLoop(freq: number, q: number, level: number, fadeIn: number, type: BiquadFilterType = 'bandpass') {
    const ctx = this.ctx!, master = this.master!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!; src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type; f.frequency.value = freq; f.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(level, ctx.currentTime + fadeIn);
    src.connect(f); f.connect(gain); gain.connect(master);
    (src as unknown as { _bp?: BiquadFilterNode })._bp = f;
    src.start();
    return { src, gain };
  }

  // ── 对外:天气 loop ──
  loop(name: LoopName | string, opts?: { fadeIn?: number }) {
    this.ensureCtx();
    if (!this.ctx || this.loops.has(name)) return;
    const fade = opts?.fadeIn ?? 2;
    if (name === 'rain') {
      // 雨:高频噪声沙沙(高通 500 + 低通斜率由 bandpass 近似)
      this.loops.set(name, this.noiseLoop(1600, 0.35, 0.075, fade, 'highpass') as never);
    } else if (name === 'storm') {
      // 暴雨:更密更沉
      this.loops.set(name, this.noiseLoop(900, 0.4, 0.11, fade, 'highpass') as never);
    } else if (name === 'wind') {
      // 大风:比底床更高一档的带通游走
      const l = this.noiseLoop(480, 1.1, 0.085, fade);
      const lfo = this.ctx.createOscillator();
      const lg = this.ctx.createGain();
      lfo.frequency.value = 0.22; lg.gain.value = 220;
      lfo.connect(lg);
      const bp = (l.src as unknown as { _bp?: BiquadFilterNode })._bp;
      if (bp) lg.connect(bp.frequency);
      lfo.start();
      this.loops.set(name, { ...l, lfo });
    }
  }

  stop(name: string, fadeOut = 2) {
    const l = this.loops.get(name);
    if (!l || !this.ctx) return;
    this.loops.delete(name);
    const t = this.ctx.currentTime;
    l.gain.gain.setValueAtTime(l.gain.gain.value, t);
    l.gain.gain.linearRampToValueAtTime(0, t + fadeOut);
    window.setTimeout(() => { try { l.src.stop(); l.lfo?.stop(); } catch { /* noop */ } }, fadeOut * 1000 + 100);
  }

  // ── 对外:one-shot ──
  play(name: OneShot | string, opts?: { delay?: number }) {
    this.ensureCtx();
    if (!this.ctx || !this.master) return;
    const at = this.ctx.currentTime + (opts?.delay ?? 0);
    switch (name) {
      case 'boom': this.boom(at); break;
      case 'thunder': this.thunder(at); break;
      case 'meteor': this.meteor(at); break;
      case 'coin': this.coin(at); break;
      case 'bell': this.bell(at); break;
      case 'carve': this.carve(at); break;
      case 'chime': this.chime(at); break;
      default: break;
    }
  }

  /** 烟花爆:低频噪声 burst + 90Hz 正弦捶胸。 */
  private boom(at: number) {
    const ctx = this.ctx!;
    this.burst(at, 900, 'lowpass', 0.32, 0.5);
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.setValueAtTime(120, at); o.frequency.exponentialRampToValueAtTime(48, at + 0.35);
    g.gain.setValueAtTime(0.28, at); g.gain.exponentialRampToValueAtTime(0.001, at + 0.4);
    o.connect(g); g.connect(this.master!); o.start(at); o.stop(at + 0.45);
  }

  /** 雷:近炸裂(高频瞬态) + 远滚雷(低频长衰减)。 */
  private thunder(at: number) {
    this.burst(at, 2600, 'highpass', 0.22, 0.09);
    this.burst(at + 0.05, 110, 'lowpass', 0.5, 2.2);
    this.burst(at + 0.7, 80, 'lowpass', 0.22, 1.6); // 二次回滚
  }

  /** 流星:双振荡下滑音 + 微亮泛音。 */
  private meteor(at: number) {
    const ctx = this.ctx!;
    for (const [f0, f1, lv] of [[1500, 320, 0.09], [2250, 480, 0.045]] as const) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(f0, at); o.frequency.exponentialRampToValueAtTime(f1, at + 0.7);
      g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(lv, at + 0.06);
      g.gain.exponentialRampToValueAtTime(0.001, at + 0.75);
      o.connect(g); g.connect(this.master!); o.start(at); o.stop(at + 0.8);
    }
  }

  /** 投币:双高音 ping(五度),清脆入账感。 */
  private coin(at: number) {
    const ctx = this.ctx!;
    [[1318, 0], [1976, 0.07]].forEach(([f, dt]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'triangle'; o.frequency.value = f;
      g.gain.setValueAtTime(0.16, at + dt); g.gain.exponentialRampToValueAtTime(0.001, at + dt + 0.35);
      o.connect(g); g.connect(this.master!); o.start(at + dt); o.stop(at + dt + 0.4);
    });
  }

  /** 纪元钟:非谐波分音簇长衰减(教堂钟质感)。 */
  private bell(at: number) {
    const ctx = this.ctx!;
    const partials: [number, number, number][] = [
      [196, 0.22, 3.6], [392, 0.13, 3.0], [523, 0.09, 2.4], [781, 0.06, 1.8], [1244, 0.035, 1.2],
    ];
    for (const [f, lv, dur] of partials) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = f * (1 + (Math.random() - 0.5) * 0.004);
      g.gain.setValueAtTime(lv, at); g.gain.exponentialRampToValueAtTime(0.001, at + dur);
      o.connect(g); g.connect(this.master!); o.start(at); o.stop(at + dur + 0.1);
    }
  }

  /** 石凿:三下短促带通噪声敲击。 */
  private carve(at: number) {
    for (let i = 0; i < 3; i++) this.burst(at + i * 0.12, 2400 + Math.random() * 600, 'bandpass', 0.14, 0.05);
  }

  /** 确认音:柔和上行三连音。 */
  private chime(at: number) {
    const ctx = this.ctx!;
    [660, 880, 1108].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      const t = at + i * 0.09;
      g.gain.setValueAtTime(0.08, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      o.connect(g); g.connect(this.master!); o.start(t); o.stop(t + 0.55);
    });
  }

  /** 噪声 burst 通用件。 */
  private burst(at: number, freq: number, type: BiquadFilterType, level: number, dur: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!;
    src.loop = dur > 1.9;
    const f = ctx.createBiquadFilter();
    f.type = type; f.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, at);
    g.gain.exponentialRampToValueAtTime(0.001, at + dur);
    src.connect(f); f.connect(g); g.connect(this.master!);
    src.start(at); src.stop(at + dur + 0.1);
  }
}

export const sfx = new SfxEngine();
